import { Form } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useEffect } from "react";
import { FormModal } from "@/shared/components/FormModal";
import { useCreatePlanting, useUpdatePlanting } from "../hooks/usePlantings";
import { usePlantingsStore } from "../store/plantings.store";
import type { CreatePlantingRequest, PlantingGeometry } from "../types";
import { PlantingForm } from "./PlantingForm";

interface PlantingFormModalProps {
	organizationId: string;
}

interface PlantingFormValues {
	organizationId: string;
	plantId: string;
	contractId?: string | null;
	territoryId: string;
	plantedAt: Dayjs;
	quantity?: number | null;
	geometry: PlantingGeometry;
}

export function PlantingFormModal({ organizationId }: PlantingFormModalProps) {
	const [form] = Form.useForm<PlantingFormValues>();
	const { modalOpen, editingItem, defaultOrganizationId, closeModal } = usePlantingsStore();

	const orgId = editingItem?.organizationId ?? defaultOrganizationId ?? organizationId;

	const { mutate: create, isPending: isCreating } = useCreatePlanting();
	const { mutate: update, isPending: isUpdating } = useUpdatePlanting();

	const close = () => {
		form.resetFields();
		closeModal();
	};

	useEffect(() => {
		if (!modalOpen) {
			return;
		}
		if (editingItem) {
			form.setFieldsValue({
				organizationId: editingItem.organizationId,
				plantId: editingItem.plantId,
				contractId: editingItem.contractId ?? undefined,
				territoryId: editingItem.territoryId ?? undefined,
				plantedAt: dayjs(editingItem.plantedAt),
				quantity: editingItem.quantity ?? undefined,
				geometry: editingItem.geometry,
			});
		} else {
			form.resetFields();
			form.setFieldsValue({ organizationId: orgId ?? undefined });
		}
	}, [modalOpen, editingItem, orgId, form]);

	const onFinish = (values: PlantingFormValues) => {
		const plantedAt = values.plantedAt.toISOString();

		if (editingItem) {
			update(
				{
					id: editingItem.id,
					data: {
						plantId: values.plantId,
						contractId: values.contractId ?? null,
						territoryId: values.territoryId,
						geometry: values.geometry,
						plantedAt,
						quantity: values.quantity ?? null,
					},
				},
				{ onSuccess: close }
			);
		} else {
			const payload: CreatePlantingRequest = {
				organizationId: values.organizationId ?? organizationId,
				plantId: values.plantId,
				contractId: values.contractId ?? undefined,
				territoryId: values.territoryId,
				geometry: values.geometry,
				plantedAt,
				quantity: values.quantity ?? undefined,
			};
			create(payload, { onSuccess: close });
		}
	};

	const handleSubmit = () => {
		form
			.validateFields()
			.then(onFinish)
			.catch(() => {
				// Validation failed - Form shows errors inline
			});
	};

	return (
		<FormModal
			open={modalOpen}
			editing={!!editingItem}
			title="Yangi ekin"
			editTitle="Ekinni tahrirlash"
			onCancel={close}
			onOk={handleSubmit}
			confirmLoading={isCreating || isUpdating}
			width={720}
		>
			<Form form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
				<PlantingForm organizationId={orgId ?? undefined} />
			</Form>
		</FormModal>
	);
}
